<script>
import baseMixin from '@/mixins/baseMixin'
import timesheetMixin from '@/mixins/timesheetMixin'
import Layout from '@layouts/main.vue'
import { mapActions, mapGetters } from 'vuex'
import {
  getWeekRangeStringFromTimesheet,
  mapPendingTimesheets,
} from '@/helpers/timesheet-helpers'
import TimesheetWeekGroup from '@/components/timesheet/timesheet-week-group.vue'
import TimesheetChangeTimeCard from '@/components/timesheet/timesheet-change-time-card.vue'
import { TimesheetOperationType } from '@/shared/constants/timesheet/TimesheetOperationType'
import TimesheetSubmitResponseViewModel from '@/models/timesheets/timesheetSubmitResponseViewModel'
import TimesheetSubmitIssuesAlert from '@/components/timesheet/timesheet-submit-issues-alert.vue'
import TimesheetSkeletonLoader from '@/components/skeleton/timesheet-skeleton-loader.vue'
import TimesheetSelectionActionBar from '@/components/timesheet/timesheet-selection-action-bar.vue'
// eslint-disable-next-line no-unused-vars
import TimesheetsPendingApprovalViewModel from '@/models/timesheets/timesheetsPendingApprovalViewModel'
import EmptyStateBlock from '@/components/general/empty-state-block.vue'

export default {
  name: 'TimesheetsView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Timesheets page.' }],
    }
  },
  components: {
    Layout,
    TimesheetWeekGroup,
    TimesheetChangeTimeCard,
    TimesheetSubmitIssuesAlert,
    TimesheetSkeletonLoader,
    TimesheetSelectionActionBar,
    EmptyStateBlock,
  },
  mixins: [baseMixin, timesheetMixin],
  data: () => {
    return {
      /**
       * @type {Array<TimesheetsPendingApprovalViewModel>}
       */
      pendingApproval: [],
      /**
       * @type {Array<TimesheetsPendingApprovalViewModel>}
       */
      selectedTimesheets: [],
      approvedCount: 0,
      pendingReviewCount: 0,
      hasLoadedSuccessfully: false,
      showChangeDialog: false,
      changeTimeTimesheet: null,
      keyOfChangeTimeTimesheet: null,
      TimesheetOperationType,
      timesheetSubmissionIssues: [],
    }
  },
  computed: {
    ...mapGetters({
      isLoadingCRUD: 'timesheets/isLoadingCRUD',
    }),
    hasSelection() {
      return this.selectedCount > 0
    },
    hasTimesheetIssues() {
      return this.timesheetSubmissionIssues.length > 0
    },
    selectedCount() {
      return this.selectedTimesheets.length || 0
    },
    pendingApprovalCount() {
      return this.pendingApproval?.length || 0
    },
    hasBookingsPendingApproval() {
      return this.pendingApprovalCount > 0
    },
    flatPendingApprovalList() {
      const test = this.pendingApproval.flatMap((pendingApprovalItem) =>
        Object.keys(pendingApprovalItem.timesheets).flatMap(
          (key) => pendingApprovalItem.timesheets[key]
        )
      )
      return test
    },
  },
  async created() {
    const result = await this.loadTimesheets()

    if (!result.isSuccess) return false

    this.initPendingApprovalList(result.data.pendingApprovalList)
    this.hasLoadedSuccessfully = true
    this.approvedCount = result.data.approvedCount
    this.pendingReviewCount = result.data.pendingReviewCount
  },
  methods: {
    ...mapActions({
      loadTimesheets: 'timesheets/loadTimesheets',
      modifyTimes: 'timesheets/updateTimes',
      cancelTimesheet: 'timesheets/cancelTimesheet',
      submitTimesheetsForApproval: 'timesheets/submitTimesheetsForApproval',
    }),
    initPendingApprovalList(timesheets) {
      this.pendingApproval = mapPendingTimesheets(timesheets, this.$i18n)
    },
    timesheetsUpdated(timesheets) {
      const flatPendingApprovalTimesheetBookingIds = this.flatPendingApprovalList?.map(
        (booking) => booking.bookingId
      )

      // Filter out timesheets that don't exist in the pending approval list.
      // Resolves a bug where selected bookings that are removed, still get
      // returned by v-data-table on select.
      const filteredTimesheets = timesheets.timesheets.filter((timesheet) =>
        flatPendingApprovalTimesheetBookingIds.includes(timesheet.bookingId)
      )

      // Since we receive only the selected timesheets from a group on a
      // selection change, it's easier to remove all the currently selected
      // timesheets from that group first, then readd them.
      this.selectedTimesheets = this.selectedTimesheets.filter(
        (selectedTimesheet) => selectedTimesheet.key !== timesheets.key
      )

      for (const timesheet of filteredTimesheets) {
        timesheet.key = timesheets.key
        this.selectedTimesheets.push(timesheet)
      }
    },
    changeTime(timesheetData) {
      this.changeTimeTimesheet = timesheetData.timesheet
      this.keyOfChangeTimeTimesheet = timesheetData.key
      this.showChangeDialog = true
    },
    async handleModifyOrDidNotHappenTimesheetSubmit(payload) {
      return this.processTimesheetSubmitResponseViewModel(
        payload.result.data,
        payload.timesheets
      )
    },
    removeProcessedTimesheetsFromSelection(processedTimesheets) {
      const processedBookingIds = processedTimesheets.map(
        (timesheet) => timesheet.bookingId
      )
      this.selectedTimesheets = this.selectedTimesheets.filter(
        (selectedTimesheet) =>
          !processedBookingIds.includes(selectedTimesheet.bookingId)
      )
    },
    /**
     * Updates counters and deselects processed timesheets
     * @param {TimesheetSubmitResponseViewModel} responseObj
     * @param {Array} flatSelectedTimesheets
     */
    processTimesheetSubmitResponseViewModel(
      responseObj,
      flatSelectedTimesheets
    ) {
      const responseVM = new TimesheetSubmitResponseViewModel(responseObj)

      this.approvedCount += responseVM.approvedForInvoiceCount
      this.pendingReviewCount += responseVM.pendingReviewCount

      // Clear selected timesheets from the UI
      for (const processedTimesheet of flatSelectedTimesheets) {
        const targetGroupTitle = getWeekRangeStringFromTimesheet(
          processedTimesheet,
          this.$i18n
        )

        // Find group containing the processed timesheet by title
        const group = this.pendingApproval.find(
          (group) => group.title === targetGroupTitle
        )

        if (!group) continue

        // Sanity check: See if processed timesheet's candidate id exists within the current group (it should)
        const doesTheTimesheetsCandidateIdExistWithinCurrentGroup = Object.prototype.hasOwnProperty.call(
          group.timesheets,
          processedTimesheet.candidateId
        )
        if (!doesTheTimesheetsCandidateIdExistWithinCurrentGroup) continue

        const indexOfTimesheetToBeRemoved = group.timesheets[
          processedTimesheet.candidateId
        ].findIndex((x) => x.bookingId === processedTimesheet.bookingId)

        if (indexOfTimesheetToBeRemoved < 0) continue

        // Remove processed timesheet row from table
        group.timesheets[processedTimesheet.candidateId].splice(
          indexOfTimesheetToBeRemoved,
          1
        )

        // If there are no more rows in the table, remove the table
        if (group.timesheets[processedTimesheet.candidateId].length === 0) {
          this.pendingApproval = this.pendingApproval.filter(
            (groupItem) => groupItem.title !== targetGroupTitle
          )
        }
      }

      this.timesheetSubmissionIssues = responseVM.errors

      this.removeProcessedTimesheetsFromSelection(flatSelectedTimesheets)
    },
    async submitTimesheets() {
      return await this.handleTimesheetSubmit(this.selectedTimesheets)
    },
    async handleTimesheetSubmit(timesheets) {
      const payload = this.mapTimesheetListToPayload(timesheets)

      const result = await this.submitTimesheetsForApproval(payload)

      if (!result?.isSuccess) return

      return this.processTimesheetSubmitResponseViewModel(
        result.data,
        timesheets
      )
    },
  },
}
</script>

<template>
  <Layout>
    <div class="d-flex align-center justify-space-between">
      <BasePageTitle
        :title="$t('timesheets.timesheetPageTitle')"
        :subtitle="$t('timesheets.timesheetPageSubtitle')"
      />
    </div>
    <TimesheetSkeletonLoader v-if="!hasLoadedSuccessfully" />
    <div v-else>
      <TimesheetSubmitIssuesAlert
        v-show="hasTimesheetIssues"
        :issues="timesheetSubmissionIssues"
      />

      <div v-if="hasBookingsPendingApproval">
        <TimesheetWeekGroup
          v-for="group of pendingApproval"
          :key="group.title"
          :group="group"
          @timesheetsUpdated="timesheetsUpdated"
          @changeTime="changeTime"
          @cancelBookingSubmitted="handleModifyOrDidNotHappenTimesheetSubmit"
        />
      </div>
      <EmptyStateBlock v-else border>
        <template v-slot:icon>
          <v-icon color="primary" class="mb-5" size="62">
            mdi-format-list-bulleted
          </v-icon>
        </template>

        <template v-slot:title>
          <h3 :class="['mb-2 font-weight-bold']">{{
            $t('timesheets.emptyState.title')
          }}</h3>
        </template>

        <template v-slot:description>
          <p :class="['text-subtitle-2']">{{
            $t('timesheets.emptyState.description')
          }}</p>
        </template>
      </EmptyStateBlock>
    </div>

    <TimesheetSelectionActionBar
      :selected-timesheets="selectedTimesheets"
      :approved-for-invoice-count="approvedCount"
      :pending-review-count="pendingReviewCount"
      @submitTimesheets="submitTimesheets"
    />

    <TimesheetChangeTimeCard
      v-model="showChangeDialog"
      :timesheet="changeTimeTimesheet"
      @closeModal="showChangeDialog = !showChangeDialog"
      @submitted="handleModifyOrDidNotHappenTimesheetSubmit"
    />
  </Layout>
</template>
